"use client";
import { FloatingPortal } from "@floating-ui/react";
import {
  autoUpdate,
  flip,
  size as floatingSize,
  offset,
  useFloating,
} from "@floating-ui/react-dom";
import {
  Label,
  Listbox,
  ListboxOptions,
  ListboxProps,
  Transition,
} from "@headlessui/react";
import clsx from "clsx";
import React, {
  DetailedReactHTMLElement,
  HTMLAttributes,
  ReactElement,
  useMemo,
} from "react";
import ErrorMessage from "../ErrorMessage";
import InfoIcon from "../InfoIcon";
import SelectButton from "./SelectButton";
import SelectOption from "./SelectOption";
import { SelectButtonProps, SelectOptionProps, SelectSize } from "./types";

export interface SelectProps<T>
  extends Omit<
    HTMLAttributes<HTMLDivElement>,
    "defaultValue" | "onChange" | "prefix"
  > {
  onChange?: (value: T) => void;
  value?: T | T[];
  className?: string;
  label?: string;
  children:
    | ReactElement<SelectButtonProps>
    | ReactElement<SelectButtonProps>[]
    | any;
  displayValue?: (value: T) => string | null;
  disabled?: boolean;
  multiple?: boolean;
  button?: ReactElement<SelectButtonProps>;
  size?: SelectSize;
  prefix?: ReactElement;
  labelHidden?: boolean;
  placeholder?: string;
  errorMessage?: string;
  defaultValue?: T | T[];
  info?: string;
  by?: ListboxProps<any, T>["by"];
}

const Select = <T,>({
  children,
  className,
  onChange,
  value,
  label,
  displayValue,
  disabled,
  multiple,
  size = "md",
  prefix,
  labelHidden,
  defaultValue,
  placeholder,
  errorMessage,
  info,
  by,
  ...props
}: SelectProps<T>) => {
  const { x, y, refs, strategy } = useFloating({
    placement: "bottom-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip(),
      floatingSize({
        apply({ availableHeight, elements, availableWidth }) {
          const rect = elements.reference.getBoundingClientRect();
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.min(availableHeight, 400) - 16}px`,
            maxWidth: `${availableWidth - 16}px`,
            width: `${rect.width}px`,
          });
        },
      }),
    ],
  });

  const options: DetailedReactHTMLElement<SelectOptionProps<T>, HTMLElement>[] =
    useMemo(
      () =>
        React.Children.map(children, (child: any) =>
          React.cloneElement(child, { size, multiple } as SelectOptionProps<T>),
        ),
      [children, size, multiple],
    );

  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : typeof value !== "undefined";

  const buttonLabel = useMemo(() => {
    let result: any = placeholder || "";
    if (multiple && Array.isArray(value) && value.length > 0) {
      result = value.map((val) => (
        <span
          key={String(val)}
          className={clsx("rounded-full border border-neutral-50", {
            "px-3xs": size === "md",
            "px-2xs leading-[18px]": size === "sm",
          })}
        >
          {displayValue ? displayValue(val) : String(val)}
        </span>
      ));
    } else if (typeof value !== "undefined") {
      if (displayValue) {
        result = displayValue(value as T);
      } else {
        const option = options.find((o) => o.props.value === value);
        result = option?.props.children || value;
      }
    }
    return result;
  }, [multiple, value, displayValue, placeholder, size, options]);

  return (
    <Listbox
      {...props}
      defaultValue={defaultValue}
      disabled={disabled}
      as="div"
      className={clsx("relative flex", className)}
      value={value}
      by={by}
      onChange={typeof onChange === "function" ? onChange : () => {}}
      multiple={multiple}
    >
      <div className="w-full">
        {label && (
          <Label
            className={clsx("mb-xs flex", {
              "text-title-1": "md",
              "text-app-title-1": "sm",
              "sr-only": labelHidden,
            })}
          >
            {label}
            {info && <InfoIcon>{info}</InfoIcon>}
          </Label>
        )}
        <SelectButton ref={refs.setReference} size={size} disabled={disabled}>
          <div
            className={clsx("flex gap-xs flex-wrap items-center text-left", {
              "text-neutral-150": !hasValue,
            })}
          >
            {prefix}
            {buttonLabel}
          </div>
        </SelectButton>
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </div>
      <FloatingPortal>
        <Transition
          as="div"
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0"
          enterTo="transform opacity-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100"
          leaveTo="transform opacity-0"
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
          }}
          className={
            "z-[51] mt-2 w-fit rounded-md bg-neutral-0 focus:outline-none rounded-s shadow-3 py-2xs flex flex-col"
          }
        >
          <ListboxOptions className="focus:outline-none overflow-auto w-full">
            {options}
          </ListboxOptions>
        </Transition>
      </FloatingPortal>
    </Listbox>
  );
};

export default Object.assign(Select, {
  Option: SelectOption,
});
